// Full-screen voice mode for the AI Agent — the driving UX: one big tap-to-toggle button,
// conversation transcript, and per-phase status. All pipeline logic lives in
// useVoiceConversation (owned by AgentPage so the transcript is shared with the chat).
import React, { useEffect, useRef } from 'react';
import { Mic, MicOff, Loader2, Volume2, X, AlertTriangle } from 'lucide-react';

const PHASE_LABELS = {
  idle: 'Tap to talk',
  listening: 'Listening — just talk (tap to pause)',
  paused: 'Paused — tap to resume',
  transcribing: 'Thinking…',
  thinking: 'Thinking…',
  speaking: 'Speaking — tap to interrupt',
  error: 'Tap to retry',
};

// Push-to-talk mode label overrides (Features:VoiceAutoDetect off).
const PTT_LABELS = {
  listening: 'Listening — tap when done',
};

const VoiceOverlay = ({ phase, errorMessage, unavailable, micTap, onClose, messages, voiceActivity, autoDetect }) => {
  const transcriptEndRef = useRef(null);

  useEffect(() => {
    transcriptEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, phase]);

  const busy = phase === 'transcribing' || phase === 'thinking';

  const buttonIcon = () => {
    if (busy) return <Loader2 className="h-12 w-12 animate-spin" />;
    if (phase === 'speaking') return <Volume2 className="h-12 w-12 animate-pulse" />;
    if (phase === 'paused') return <MicOff className="h-12 w-12" />;
    if (phase === 'error') return <AlertTriangle className="h-12 w-12" />;
    return <Mic className="h-12 w-12" />;
  };

  const buttonClasses = () => {
    if (phase === 'listening')
      // Auto-detect gets the live level ring; push-to-talk keeps the classic pulse.
      return `bg-risk-critical text-white ring-8 ring-risk-critical/30${autoDetect ? '' : ' animate-pulse'}`;
    if (phase === 'speaking') return 'bg-accent-cyan text-white ring-8 ring-accent-cyan/30';
    if (phase === 'paused')
      return 'bg-theme-secondary text-content-secondary border border-border hover:text-content-primary';
    if (phase === 'error') return 'bg-theme-secondary text-risk-critical border border-risk-critical/40';
    return 'bg-gradient-primary text-white hover:opacity-90';
  };

  return (
    <div className="fixed inset-0 z-50 bg-theme-primary flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-border">
        <div>
          <h2 className="text-lg font-semibold text-content-primary">Voice Mode</h2>
          <p className="text-xs text-content-secondary">Talk with your risk data</p>
        </div>
        <button
          onClick={onClose}
          aria-label="Close voice mode"
          className="flex items-center justify-center w-9 h-9 text-content-muted hover:text-content-primary border border-border rounded-lg hover:bg-theme-secondary transition-colors"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {/* Transcript */}
      <div className="flex-1 overflow-y-auto px-6 py-4 space-y-3 max-w-2xl w-full mx-auto min-h-0">
        {messages.length === 0 && (
          <div className="flex items-center justify-center h-full text-content-muted text-sm">
            Tap the button and ask a question
          </div>
        )}
        {messages.map((msg, i) => (
          <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div
              className={`max-w-[85%] rounded-xl px-4 py-2.5 text-sm whitespace-pre-wrap ${
                msg.role === 'user'
                  ? 'bg-accent-cyan/10 border border-accent-cyan/20 text-content-primary'
                  : 'bg-theme-secondary border border-border text-content-primary'
              }`}
            >
              {msg.role === 'assistant' ? msg.meta?.spokenAnswer || msg.content : msg.content}
            </div>
          </div>
        ))}
        <div ref={transcriptEndRef} />
      </div>

      {/* Status + big button */}
      <div className="flex flex-col items-center gap-4 px-6 pb-10 pt-4">
        {errorMessage && (
          <p className="text-sm text-risk-critical text-center max-w-md">{errorMessage}</p>
        )}
        <div className="relative flex items-center justify-center">
          {autoDetect && phase === 'listening' && (
            <span
              aria-hidden="true"
              className="absolute inset-0 rounded-full bg-risk-critical/25 transition-transform duration-100"
              style={{ transform: `scale(${1 + (voiceActivity?.level || 0) * 0.35})` }}
            />
          )}
          {autoDetect && phase === 'listening' && (voiceActivity?.sendProgress || 0) > 0 && (
            <svg aria-hidden="true" className="absolute -inset-2 -rotate-90 text-accent-cyan" viewBox="0 0 128 128">
              <circle
                cx="64" cy="64" r="60" fill="none" stroke="currentColor" strokeWidth="4"
                strokeDasharray={2 * Math.PI * 60}
                strokeDashoffset={2 * Math.PI * 60 * (1 - voiceActivity.sendProgress)}
                strokeLinecap="round"
              />
            </svg>
          )}
          <button
            onClick={micTap}
            disabled={unavailable}
            aria-label={PHASE_LABELS[phase] || 'Voice'}
            className={`relative flex items-center justify-center w-28 h-28 rounded-full transition-all disabled:opacity-40 ${buttonClasses()}`}
          >
            {buttonIcon()}
          </button>
        </div>
        <p className="text-sm text-content-secondary">
          {unavailable
            ? 'Voice unavailable'
            : autoDetect && phase === 'listening' && (voiceActivity?.sendProgress || 0) > 0
              ? 'Sending…'
              : (!autoDetect && PTT_LABELS[phase]) || PHASE_LABELS[phase] || ''}
        </p>
      </div>
    </div>
  );
};

export default VoiceOverlay;
